import useUrlState from '@ahooksjs/use-url-state'
import { InfoCircleOutlined, LeftOutlined, LockOutlined, PlusCircleOutlined, RightOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Avatar, Button, DatePicker, Drawer, Form, Input, Popconfirm, Popover, Table, Tooltip, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'
import { produce } from 'immer'
import { useAtom } from 'jotai'
import { isEqual, isNil, isNull, omit, omitBy, partition, pick, uniq } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { v4 } from 'uuid'

import { getPointPermission, UsersSearch } from '@/components'
import { Customer, CustomerFlowStatus } from '@/modules/customer/customer.schema'
import { Devlop, DevlopStatus } from '@/modules/devlop/devlop.schema'
import { Production, ProductionFlowStatus } from '@/modules/production-project/production.schema'
import { userInfoAtom, usersAtom } from '@/modules/user'
import { PAGE_RESOURCE_POINTS_ENUM, ResourceType, SpecificProjectWorkResource, WorkResourceRowType } from '@/schemas'

import ss from '../click-away/click-away.module.less'
import { ClickAwayDepartmentSearch } from '../click-away/click-away-department-search.component'
// import { ClickAwayInputNumber } from '../click-away/click-away-input-number.component'
// import { ClickAwayRangePicker } from '../click-away/click-away-range-picker.component'
import { ClickAwaySubProjectCustomSearch } from '../click-away/click-away-subproject-custom-search.component'
import { ClickAwayUserSearch } from '../click-away/click-away-user-search.component'
import { ClickAwayWeekInputNumber } from '../click-away/click-away-week-input-number.component'
import s from './special-project-work-resource.module.less'
import {
  getCreatingRecordFromTree,
  getCustomIsColumnRequired,
  getIsDevelopmentColumnRequired,
  getIsProductionColumnRequired,
  getIsValidWeekToUpsert,
  getParentByKey,
  getRecordByKey,
  ProjectTableColumnKey,
  setCreatingRecordToSubTree,
  validateFields,
} from './special-project-work-resource.util'
import { TdCell } from './td-cell.component'

export interface ProjectWorkResourceInput extends SpecificProjectWorkResource {
  isCreating?: boolean
  newSubprojectWorkResourceSelfId?: string
  parentKey?: string | null
}

export const SpecialProjectWorkResourceContainer: React.FC<{
  resource: Customer | Production | Devlop
  resourceType: ResourceType
  status?: CustomerFlowStatus | ProductionFlowStatus | DevlopStatus
  onLoading?: (value: boolean) => void
  onSetWorkResources?: (workResources: SpecificProjectWorkResource[]) => void
}> = React.forwardRef(({ status: inputStatus, resourceType, resource, onLoading, onSetWorkResources }, ref) => {
  const [viewer] = useAtom(userInfoAtom)
  const isSuperAdmin = viewer.isSuperAdmin
  const { t } = useTranslation()
  const { canCreate, canUpdate, canDelete } = getPointPermission(
    PAGE_RESOURCE_POINTS_ENUM.资源管理,
    resource.permissions
  )
  const { selfId } = resource
  let status = 'flowStatus' in resource ? resource.flowStatus : resource.status
  status = inputStatus || status

  const [users] = useAtom(usersAtom)
  const location = useLocation()
  const urlParams = new URLSearchParams(location.search)
  const urlYear = urlParams.get('year')
  const urlWeek = urlParams.get('week')

  const getIsColumnRequired =
    resourceType === ResourceType.DEVLOP
      ? getIsDevelopmentColumnRequired
      : resourceType === ResourceType.CUSTOMER
        ? getCustomIsColumnRequired
        : getIsProductionColumnRequired

  const [openDrawer, setOpenDrawer] = React.useState<{ record: ProjectWorkResourceInput; readonly: boolean }>()
  const [drawerContent, setDrawerContent] = React.useState('')
  const [records, setRecords] = React.useState<ProjectWorkResourceInput[]>([])
  const [expandKeys, setExpandKeys] = React.useState<string[]>([])
  const initMomentDate = moment().isoWeekday() < 3 ? moment().subtract(1, 'week') : moment()
  const [state, setState] = useUrlState({
    year: urlYear || initMomentDate.isoWeekYear(),
    week: urlWeek || initMomentDate.isoWeek(),
  })

  const [weekDate, setWeekDate] = React.useState<{
    year: number
    week: number
    startDate: moment.Moment
    endDate: moment.Moment
  }>({
    year: initMomentDate.isoWeekYear(),
    week: initMomentDate.isoWeek(),
    startDate: initMomentDate.startOf('isoWeek'),
    endDate: initMomentDate.endOf('isoWeek'),
  })

  const {
    data,
    refresh,
    loading: getLoading,
  } = useRequest<{
    projectWorkResourceSelfId?: string
    workResources: SpecificProjectWorkResource[]
  }>(
    () => ({
      url: `/api/work-resource/getSpecialProjectWorkResource?${new URLSearchParams({
        projectSelfId: selfId,
        resourceType: resourceType.toString(),
        year: weekDate.year.toString(),
        week: weekDate.week.toString(),
        startDate: weekDate.startDate.format('YYYY-MM-DD'),
        endDate: weekDate.endDate.format('YYYY-MM-DD'),
      })}`,
      method: 'get',
    }),
    { refreshDeps: [weekDate] }
  )

  const { run, loading: creatingLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/createSpecificProjectUserWorkResource`,
      data: {
        ...data,
        resourceType,
        projectSelfId: selfId,
      },
      method: 'post',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: deleteRun, loading: deleteLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/deleteWorkResource?${new URLSearchParams(data)}`,
      method: 'delete',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: updateRun, loading: updateLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/updateWorkResource',
      method: 'patch',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: commentRun, loading: commentLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/upsertWeekWorkResourceComment',
      method: 'post',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  React.useImperativeHandle(
    ref,
    () => ({
      workResourceRefresh: () => {
        refresh()
      },
    }),
    [weekDate]
  )

  const projectWorkResourceSelfId = data?.projectWorkResourceSelfId || null
  const isProjectEnded =
    resourceType === ResourceType.CUSTOMER
      ? [
          CustomerFlowStatus.项目已取消,
          CustomerFlowStatus.项目已失败,
          CustomerFlowStatus.项目已结项,
          CustomerFlowStatus.项目已驳回,
        ].includes(status as CustomerFlowStatus)
      : resourceType === ResourceType.DEVLOP
        ? status === DevlopStatus.已结项
        : [ProductionFlowStatus.项目已关闭, ProductionFlowStatus.项目已结项, ProductionFlowStatus.项目已驳回].includes(
            status as ProductionFlowStatus
          )

  React.useEffect(() => {
    // 防止 invalid year, week
    const year = !urlYear || isNaN(Number(urlYear)) ? initMomentDate.isoWeekYear() : Number(urlYear)
    const week = !urlWeek || isNaN(Number(urlWeek)) ? initMomentDate.isoWeek() : Number(urlWeek)
    const date = initMomentDate.clone().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
    // 防止越界 week, 如 week=60
    setState({ year: date.isoWeekYear(), week: date.isoWeek() })
  }, [])

  React.useEffect(() => {
    const year = state.year
    const week = state.week
    const startDate = initMomentDate.clone().isoWeekYear(year).isoWeek(week).startOf('isoWeek')
    const endDate = initMomentDate.clone().isoWeekYear(year).isoWeek(week).endOf('isoWeek')
    setWeekDate({ year, week, startDate, endDate })
  }, [state])

  React.useEffect(() => {
    if (data?.workResources) {
      setRecords((prev) => {
        const creatingData: ProjectWorkResourceInput[] = []
        getCreatingRecordFromTree(prev, creatingData)
        const [rootCreatingData, subTreeCreatingData] = partition(creatingData, (d) => isNull(d.parentKey))

        const newState = produce(data.workResources, (draft) => {
          setCreatingRecordToSubTree(draft, subTreeCreatingData)
        })
        return [...(newState as ProjectWorkResourceInput[]), ...rootCreatingData]
      })
      // 外部组件校验当前数据是否符合要求
      if (onSetWorkResources) {
        onSetWorkResources(data.workResources)
      }
    }
  }, [data])

  React.useEffect(() => {
    if (onLoading) {
      onLoading(getLoading || creatingLoading || deleteLoading || updateLoading || commentLoading)
    }
  }, [getLoading, creatingLoading, deleteLoading, updateLoading])

  React.useEffect(() => {
    const week = openDrawer?.record?.weeks?.[0]
    setDrawerContent(week?.comment?.comment || '')
  }, [openDrawer])

  const columns: ColumnType<ProjectWorkResourceInput>[] = [
    {
      title: (
        <>
          <Form.Item
            style={{ marginBottom: 0 }}
            required={getIsColumnRequired(ProjectTableColumnKey.Subproject, status)}
            label={t('work.resource.table.title.subproject')}
            colon={false}
          />
        </>
      ),
      dataIndex: ProjectTableColumnKey.Subproject,
      key: ProjectTableColumnKey.Subproject,
      width: 320,
      fixed: 'left',
      render: (_, record) => {
        const readonly = record.isArchived || !canUpdate

        return (
          <div>
            <ClickAwaySubProjectCustomSearch
              readonly={readonly}
              projectWorkResourceSelfId={projectWorkResourceSelfId || undefined}
              record={record}
              onCreate={() => {
                setRecords((prev) => {
                  const newState = produce(prev, (draft) => {
                    // 获取当前节点
                    const records: ProjectWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, records)
                    const [currentRecord] = records

                    const newRecord = {
                      key: `${currentRecord.key}-${v4()}`,
                      type: WorkResourceRowType.User,
                      subprojectWorkResourceSelfId: currentRecord.subprojectWorkResourceSelfId,
                      subprojectName: currentRecord.subprojectName,
                      subprojectSelfId: currentRecord.subprojectWorkResourceSelfId || record.project?.selfId || null,
                      userWorkResourceSelfId: '',
                      departmentId: 0,
                      departmentName: '',
                      email: '',
                      startDate: (resource.startDate
                        ? moment(resource.startDate).format('YYYY-MM-DD')
                        : undefined) as any,
                      endDate: (resource.endDate ? moment(resource.endDate).format('YYYY-MM-DD') : undefined) as any,
                      predictWorkload: null,
                      actualWorkload: null,
                      project: record?.project,
                      quarters: [],
                      weeks: [],
                      isCreating: true,
                      parentKey: currentRecord.key,
                    }
                    currentRecord['children'] = currentRecord.children
                      ? [newRecord, ...currentRecord.children]
                      : [newRecord]
                  })
                  return newState
                })
                setExpandKeys((prev) => uniq([...prev, record.key]))
              }}
              onChange={({ subprojectWorkResourceName, subprojectWorkResourceSelfId }) => {
                // 创建
                if (record.isCreating) {
                  setRecords((prev) =>
                    produce(prev, (draft) => {
                      const currentRecords: ProjectWorkResourceInput[] = []
                      getRecordByKey(record.key, draft, currentRecords)
                      const [currentRecord] = currentRecords

                      if (currentRecord) {
                        currentRecord.subprojectName = subprojectWorkResourceName
                        currentRecord.subprojectSelfId = subprojectWorkResourceSelfId || null
                        currentRecord.subprojectWorkResourceSelfId = subprojectWorkResourceSelfId || null
                      }
                    })
                  )
                  // 更新
                } else {
                  updateRun({
                    projectWorkResourceSelfId,
                    subprojectWorkResourceSelfId: record.subprojectWorkResourceSelfId,
                    subprojectName: subprojectWorkResourceName,
                  })
                }
              }}
            />
          </div>
        )
      },
    },
    {
      title: (
        <Form.Item
          style={{ marginBottom: 0 }}
          label={t('work.resource.table.title.department')}
          required={getIsColumnRequired(ProjectTableColumnKey.Department, status)}
          colon={false}
        />
      ),
      dataIndex: ProjectTableColumnKey.Department,
      key: ProjectTableColumnKey.Department,
      width: 160,
      render: (_, record) => {
        const isDepartmentRow = record.type === WorkResourceRowType.Department
        const isSubprojectRow = [WorkResourceRowType.Project, WorkResourceRowType.Custom].includes(record.type)
        const isPortfolio = WorkResourceRowType.Portfolio === record.type

        const isActive = record.isCreating

        if (isSubprojectRow || isPortfolio) {
          const names = uniq(((isPortfolio ? record.departments : record.children) || []).map((r) => r.departmentName))
          return (
            <Tooltip title={names.join(', ')}>
              <div className='w-[140px]'>
                {names.slice(0, 2).map((name) => (
                  <div key={name} className='whitespace-nowrap overflow-hidden overflow-ellipsis'>
                    {name}
                  </div>
                ))}
                {names.length > 2 && <div key='others'>...</div>}
              </div>
            </Tooltip>
          )
        }
        const readonly = record.isArchived || !canUpdate
        const includeEmails = record.departmentId
          ? users.filter((u) => u.departmentIds.includes(record.departmentId))
          : undefined
        return (
          <div className='flex gap-2'>
            <ClickAwayDepartmentSearch
              iconClassName={isDepartmentRow ? '' : ss.displayDepartment}
              wrapperClassName={isDepartmentRow ? '' : ss.departmentVisibility}
              required={getIsColumnRequired(ProjectTableColumnKey.Department, status)}
              readonly={readonly}
              isActive={isActive}
              name={record.departmentName}
              id={record.departmentId}
              onChange={(value, label) => {
                setRecords((prev) =>
                  produce(prev, (draft) => {
                    const currentRecords: ProjectWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, currentRecords)
                    const [currentRecord] = currentRecords

                    if (currentRecord) {
                      currentRecord.departmentId = value
                      currentRecord.departmentName = label?.[0] || ''
                    }
                  })
                )
                // 记录存在直接更新
                if (record.userWorkResourceSelfId) {
                  updateRun({
                    projectWorkResourceSelfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    departmentId: value,
                    departmentName: label?.[0] || '',
                  })
                }
              }}
            />
            {isDepartmentRow && !record.isCreating && !record.isArchived && (
              <span>
                {canCreate ? (
                  <Popover
                    trigger='click'
                    title={
                      <Typography.Text className='font-medium text-[16px] text-[#191919] leading-[24px]'>
                        {t('add users')}
                      </Typography.Text>
                    }
                    content={
                      <div>
                        <UsersSearch
                          onChange={(v) => {
                            if (v) {
                              setRecords((prev) => {
                                const newState = produce(prev, (draft) => {
                                  // 获取当前节点
                                  const records: ProjectWorkResourceInput[] = []
                                  getRecordByKey(record.key, draft, records)
                                  const [draftRecord] = records

                                  // 获取 parent
                                  const parents: ProjectWorkResourceInput[] = []
                                  getParentByKey(record.key, draft, parents)
                                  const [parent] = parents

                                  const newItem = {
                                    key: `${draftRecord.key}-${v4()}`,
                                    type: WorkResourceRowType.User,
                                    subprojectWorkResourceSelfId: parent.subprojectWorkResourceSelfId,
                                    userWorkResourceSelfId: '',
                                    subprojectName: parent!.subprojectName || '',
                                    subprojectSelfId: null,
                                    departmentId: draftRecord.departmentId,
                                    departmentName: draftRecord.departmentName,
                                    email: v,
                                    startDate: (resource.startDate
                                      ? moment(resource.startDate).format('YYYY-MM-DD')
                                      : undefined) as any,
                                    endDate: (resource.endDate
                                      ? moment(resource.endDate).format('YYYY-MM-DD')
                                      : undefined) as any,
                                    predictWorkload: null,
                                    actualWorkload: null,
                                    project: parent!.project,
                                    quarters: [],
                                    weeks: [],
                                    isCreating: true,
                                    parentKey: draftRecord.key,
                                  }
                                  draftRecord['children'] = draftRecord?.children
                                    ? [newItem, ...draftRecord.children]
                                    : [newItem]
                                })

                                return newState
                              })
                              setExpandKeys((prev) => uniq([...prev, record.key]))
                            }
                          }}
                          includeEmails={includeEmails}
                          placeholder={t('search users')}
                          style={{ width: 200 }}
                        />
                      </div>
                    }
                  >
                    <PlusCircleOutlined />
                  </Popover>
                ) : (
                  <PlusCircleOutlined className='cursor-not-allowed' />
                )}
              </span>
            )}
          </div>
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.children, prev.children) ||
        curr.departmentId !== prev.departmentId ||
        curr.departmentName !== prev.departmentName ||
        curr.email !== prev.departmentName ||
        curr.isCreating !== prev.isCreating,
    },
    {
      title: (
        <Form.Item
          style={{ marginBottom: 0 }}
          label={t('work.resource.table.title.people')}
          required={getIsColumnRequired(ProjectTableColumnKey.Email, status)}
          colon={false}
        />
      ),
      dataIndex: ProjectTableColumnKey.Email,
      key: ProjectTableColumnKey.Email,
      width: 160,
      render: (value, record) => {
        const isCreating = record.isCreating

        if (
          [WorkResourceRowType.Project, WorkResourceRowType.Custom, WorkResourceRowType.Portfolio].includes(
            record.type
          ) &&
          !isCreating
        ) {
          const emails =
            record.type === WorkResourceRowType.Portfolio
              ? record.users || []
              : (record.children || []).flatMap((child) => child.children || []).map((child) => child.email)
          const filteredUsers = users.filter((u) => emails.includes(u.email))
          return (
            <Avatar.Group
              size='small'
              maxCount={5}
              maxStyle={{
                color: '#f56a00',
                backgroundColor: '#fde3cf',
                cursor: 'pointer',
              }}
            >
              {filteredUsers.map((u) => {
                return (
                  <Tooltip key={u.email} title={u.name}>
                    <Avatar src={u.avatar} className='cursor-pointer' />
                  </Tooltip>
                )
              })}
            </Avatar.Group>
          )
        }

        if (record.type === WorkResourceRowType.Department && !isCreating) {
          const emails = (record.children || []).map((r) => r.email)
          const filteredUsers = users.filter((u) => emails.includes(u.email))
          return (
            <Avatar.Group
              size='small'
              maxCount={5}
              maxStyle={{
                color: '#f56a00',
                backgroundColor: '#fde3cf',
                cursor: 'pointer',
              }}
            >
              {filteredUsers.map((u) => {
                return (
                  <Tooltip key={u.email} title={u.name}>
                    <Avatar src={u.avatar} className='cursor-pointer' />
                  </Tooltip>
                )
              })}
            </Avatar.Group>
          )
        }

        if (record.type !== WorkResourceRowType.User && !isCreating) {
          return null
        }
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
        const readonly = record.isArchived || !canUpdate || isUserNotEmployee
        const includeEmails = record.departmentId
          ? users.filter((u) => u.departmentIds.includes(record.departmentId))
          : undefined
        return (
          <ClickAwayUserSearch
            includeEmails={includeEmails}
            required={getIsColumnRequired(ProjectTableColumnKey.Email, status)}
            isActive={isCreating}
            record={record}
            value={value}
            readonly={readonly}
            onChange={(v) => {
              setRecords((prev) =>
                produce(prev, (draft) => {
                  const user = users.find((u) => u.email === v)
                  const currentRecords: ProjectWorkResourceInput[] = []
                  getRecordByKey(record.key, draft, currentRecords)
                  const [currentRecord] = currentRecords

                  if (currentRecord) {
                    currentRecord.email = v
                    const department = user?.departments[0]
                    if (department) {
                      currentRecord.departmentId = currentRecord.departmentId || department?.id || 0
                      currentRecord.departmentName = currentRecord.departmentName || department?.name || ''
                    } else {
                      currentRecord.departmentId = 0
                      currentRecord.departmentName = ''
                    }
                  }
                })
              )
              if (record.userWorkResourceSelfId) {
                updateRun({
                  projectWorkResourceSelfId,
                  userWorkResourceSelfId: record.userWorkResourceSelfId,
                  email: v,
                })
              }
            }}
          />
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.children, prev.children) ||
        curr.email !== prev.email ||
        curr.departmentId !== prev.departmentId ||
        curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: (
    //     <Form.Item
    //       style={{ marginBottom: 0 }}
    //       label={t('work.resource.table.title.predictWorkload')}
    //       required={getIsColumnRequired(ProjectTableColumnKey.PredictWorkload, status)}
    //       colon={false}
    //     />
    //   ),
    //   dataIndex: ProjectTableColumnKey.PredictWorkload,
    //   key: ProjectTableColumnKey.PredictWorkload,
    //   width: 120,
    //   render: (value, record) => {
    //     const isUserNotEmployee =
    //       record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
    //     const readonly =
    //       record.isArchived ||
    //       (!record.isCreating && record.type !== WorkResourceRowType.User) ||
    //       record.type === WorkResourceRowType.Portfolio ||
    //       !canUpdate ||
    //       isUserNotEmployee
    //     return (
    //       <ClickAwayInputNumber
    //         key='predict'
    //         required={getIsColumnRequired(ProjectTableColumnKey.PredictWorkload, status)}
    //         readonly={readonly}
    //         isActive={record.isCreating}
    //         value={value}
    //         onUpdate={(v) => {
    //           updateRun({
    //             projectWorkResourceSelfId,
    //             userWorkResourceSelfId: record.userWorkResourceSelfId,
    //             predictWorkload: Number(v),
    //           })
    //         }}
    //         onChange={(v) => {
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: ProjectWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.predictWorkload = !isNil(v) ? Number(v) : null
    //               }
    //             })
    //           )
    //         }}
    //       />
    //     )
    //   },
    //   shouldCellUpdate: (curr, prev) => {
    //     return Number(curr.predictWorkload) !== Number(prev.predictWorkload) || curr.isCreating !== prev.isCreating
    //   },
    // },
    {
      title: (
        <Form.Item style={{ marginBottom: 0 }} label={t('work.resource.table.title.actualWorkload')} colon={false} />
      ),
      dataIndex: ProjectTableColumnKey.ActualWorkload,
      key: ProjectTableColumnKey.ActualWorkload,
      width: 120,
      render: (_, record) => {
        return (
          <span className={ss.readonlyText}>
            {!isNil(record.actualWorkload) ? Number(record.actualWorkload) : '待评估'}
          </span>
        )
      },
      shouldCellUpdate: (curr, prev) =>
        Number(curr.actualWorkload) !== Number(prev.actualWorkload) || curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: (
    //     <Form.Item
    //       style={{ marginBottom: 0 }}
    //       label={t('work.resource.table.title.period')}
    //       required={true}
    //       colon={false}
    //     />
    //   ),
    //   dataIndex: ProjectTableColumnKey.Period,
    //   key: ProjectTableColumnKey.Period,
    //   width: 280,
    //   shouldCellUpdate: (curr, prev) =>
    //     curr.startDate !== prev.startDate ||
    //     curr.endDate !== prev.endDate ||
    //     curr.isCreating !== prev.isCreating ||
    //     curr.email !== prev.email,
    //   render: (_, record) => {
    //     const isUserNotEmployee =
    //       record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
    //     const readonly =
    //       record.isArchived ||
    //       record.type === WorkResourceRowType.Portfolio ||
    //       (!record.isCreating && record.type !== WorkResourceRowType.User) ||
    //       (record.isCreating && record.type === WorkResourceRowType.Department && !record.email) || // 只创建部门时使用默认周期
    //       !canUpdate ||
    //       isUserNotEmployee
    //     return (
    //       <ClickAwayRangePicker
    //         required={true}
    //         isActive={record.isCreating}
    //         readonly={readonly}
    //         startDate={record.startDate ? moment(record.startDate) : undefined}
    //         endDate={record.endDate ? moment(record.endDate) : undefined}
    //         onChange={(_, dateStrings) => {
    //           const [startDate, endDate] = dateStrings
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: ProjectWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.startDate = startDate
    //                 currentRecord.endDate = endDate
    //               }
    //             })
    //           )
    //           if (record.userWorkResourceSelfId && startDate && endDate) {
    //             updateRun({
    //               projectWorkResourceSelfId,
    //               userWorkResourceSelfId: record.userWorkResourceSelfId,
    //               startDate: startDate,
    //               endDate: endDate,
    //             })
    //           }
    //         }}
    //       />
    //     )
    //   },
    // },
    {
      title: (
        <Form.Item
          style={{ marginBottom: 0 }}
          label={t('work.resource.table.title.weekWorkload')}
          required={getIsColumnRequired(ProjectTableColumnKey.WeekWorkload, status)}
          colon={false}
        />
      ),
      width: 160,
      dataIndex: ProjectTableColumnKey.WeekWorkload,
      key: ProjectTableColumnKey.WeekWorkload,
      // cell 更新受到 weekDate state 影响，这里不检查更新逻辑
      // shouldCellUpdate: (curr, prev) =>
      //   !isEqual(curr, prev) ||
      //   curr.startDate !== prev.startDate ||
      //   curr.endDate !== prev.endDate ||
      //   curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const week = record.weeks.find(
          (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
        ) || {
          year: weekDate.year.toString(),
          week: weekDate.week.toString(),
          startDate: weekDate.startDate.format('YYYY-MM-DD'),
          endDate: weekDate.endDate.format('YYYY-MM-DD'),
          workload: null,
        }

        const shouldWeekBeArchived =
          !getIsValidWeekToUpsert({
            year: Number(week.year),
            week: Number(week.week),
          }) || week.isArchived

        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating
        const readonly =
          record.isArchived ||
          (!record.isCreating && record.type !== WorkResourceRowType.User) ||
          record.type === WorkResourceRowType.Portfolio ||
          !canUpdate ||
          isUserNotEmployee ||
          shouldWeekBeArchived

        const existWeek = record?.weeks?.[0]
        return (
          <div className='flex gap-2 items-center'>
            <ClickAwayWeekInputNumber
              key={`${record.key}-${week.year}-${week.week}`}
              year={weekDate.year}
              week={weekDate.week}
              value={week}
              showLockedIcon={shouldWeekBeArchived}
              readonly={readonly}
              isActive={record.isCreating}
              required={getIsColumnRequired(ProjectTableColumnKey.WeekWorkload, status)}
              onUpdate={(value) => {
                if (record.userWorkResourceSelfId) {
                  const weekToUpdate = { ...week, workload: Number(value) }
                  updateRun({
                    projectWorkResourceSelfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    week: weekToUpdate,
                  })
                }
              }}
              onChange={(value) => {
                setRecords((prev) =>
                  produce(prev, (draft) => {
                    const currentRecords: ProjectWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, currentRecords)
                    const [currentRecord] = currentRecords

                    if (currentRecord) {
                      const index = currentRecord.weeks.findIndex((q) => q.week === week.week && q.year === week.year)
                      if (~index) {
                        currentRecord.weeks[index] = {
                          ...week,
                          workload: Number(value),
                        }
                      } else {
                        currentRecord.weeks = [{ ...week, workload: Number(value) }]
                      }
                    }
                  })
                )
              }}
            />
            {record.type === WorkResourceRowType.User && existWeek?.comment?.comment && (
              <Tooltip title='备注'>
                <InfoCircleOutlined
                  className='cursor-pointer'
                  onClick={() => {
                    setOpenDrawer({ record, readonly: true })
                  }}
                />
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      title: t('work.resource.table.title.operation'),
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
      fixed: 'right',
      // shouldCellUpdate: (curr, prev) => !isEqual(curr, prev) || curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const isPortfolio = record.type === WorkResourceRowType.Portfolio
        const isProject = record.type === WorkResourceRowType.Project
        const isUserNotEmployee =
          record.type === WorkResourceRowType.User && users.every((u) => u.email !== record.email) && !record.isCreating

        if (record.isArchived || isUserNotEmployee) {
          return <LockOutlined />
        }

        if (isPortfolio || isProject) {
          return null
        }

        if (record.isCreating) {
          return (
            <div className='flex justify-between'>
              <Button
                type='link'
                onClick={() => {
                  const data = {
                    ...omit(record, ['isCreating', 'key']),
                    projectWorkResourceSelfId,
                  }
                  if (validateFields(data, getIsColumnRequired, status)) {
                    run(data)
                    setRecords((prev) => {
                      const newState = produce(prev, (draft) => {
                        const parents: ProjectWorkResourceInput[] = []
                        getParentByKey(record.key, draft, parents)
                        const [parent] = parents
                        // 非 root 节点
                        if (parent) {
                          parent['children'] = (parent.children || []).filter((child) => child.key !== record.key)
                        } else {
                          // root 节点
                          return draft.filter((d) => d.key !== record.key)
                        }
                      })
                      return newState
                    })
                  }
                }}
              >
                {t('save')}
              </Button>
              <Button
                type='link'
                onClick={() => {
                  setRecords((prev) => {
                    const newState = produce(prev, (draft) => {
                      const parents: ProjectWorkResourceInput[] = []
                      getParentByKey(record.key, draft, parents)
                      const [parent] = parents
                      // 非 root 节点
                      if (parent) {
                        parent['children'] = (parent.children || []).filter((child) => child.key !== record.key)
                      } else {
                        // root 节点
                        return draft.filter((d) => d.key !== record.key)
                      }
                    })
                    return newState
                  })
                }}
              >
                {t('cancel')}
              </Button>
            </div>
          )
        }

        const commentButton =
          record.type === WorkResourceRowType.User ? (
            <Button
              type='link'
              onClick={() => {
                setOpenDrawer({ record, readonly: false })
              }}
              disabled={!canUpdate}
            >
              {t('work.resource.comment')}
            </Button>
          ) : null

        const deleteButton = isSuperAdmin ? (
          <Popconfirm
            title={t(`work.resource.${record.type}.deleteConfirm`)}
            onConfirm={() => {
              const data = omitBy(pick(record, ['subprojectWorkResourceSelfId', 'userWorkResourceSelfId']), isNil)
              deleteRun({ ...data, projectWorkResourceSelfId })
            }}
            disabled={!canDelete}
          >
            <Button type='text' danger disabled={!canDelete}>
              {t('remove')}
            </Button>
          </Popconfirm>
        ) : null

        const week = record.weeks.find(
          (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
        )
        const invisibleButton =
          record.type === WorkResourceRowType.User &&
          canUpdate &&
          !record.invisibleDate &&
          !Number(week?.workload) &&
          moment().isBetween(weekDate.startDate, weekDate.endDate, 'date', '[]') ? (
            <Popconfirm
              title={
                <div className='max-w-[300px]'>
                  <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm1')}</div>
                  <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm2')}</div>
                </div>
              }
              onConfirm={() => {
                updateRun({
                  projectWorkResourceSelfId,
                  userWorkResourceSelfId: record.userWorkResourceSelfId,
                  invisibleDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
                })
              }}
            >
              <Button type='link'>{t('work.resource.invisible.title')}</Button>
            </Popconfirm>
          ) : null

        return (
          <div className='flex gap-2'>
            {commentButton}
            {invisibleButton}
            {deleteButton}
          </div>
        )
      },
    },
  ]

  const canAdd = ['wangzhaoren@agora.io', 'yangyang@agora.io'].includes(viewer.email)
  return (
    <div>
      <div className='flex mb-4 gap-4 justify-start'>
        <DatePicker
          allowClear={false}
          picker='week'
          format={`${weekDate.startDate.format('YYYY-MM-DD')}-${weekDate.endDate.format('YYYY-MM-DD')} (GGGG-Wo)`}
          value={moment(weekDate.startDate)}
          onChange={(value) => {
            if (value) {
              const year = value.isoWeekYear()
              const week = value.isoWeek()
              setState({ year, week })
            }
          }}
        />
        <Button
          onClick={() => {
            const yearWeek = moment().isoWeekYear(state.year).isoWeek(state.week).subtract(1, 'week')
            const year = yearWeek.isoWeekYear()
            const week = yearWeek.isoWeek()
            setState({ year, week })
          }}
          icon={<LeftOutlined />}
        />
        <Button
          onClick={() => {
            const yearWeek = moment().isoWeekYear(state.year).isoWeek(state.week).add(1, 'week')
            const year = yearWeek.isoWeekYear()
            const week = yearWeek.isoWeek()
            setState({ year, week })
          }}
          icon={<RightOutlined />}
        />
      </div>
      <Table
        rowClassName={(record) => {
          if (record.type === WorkResourceRowType.Department) {
            return s.departmentRow
          }
          if ([WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)) {
            return s.subprojectRow
          }
          if (record.type === WorkResourceRowType.Portfolio) {
            return s.portfolioRow
          }
          return s.row
        }}
        scroll={{ x: 'max-content' }}
        components={{
          body: { cell: TdCell },
        }}
        loading={getLoading || creatingLoading || deleteLoading || updateLoading}
        pagination={false}
        bordered
        expandable={{
          expandedRowKeys: expandKeys,
          onExpandedRowsChange: (keys) => setExpandKeys(keys as string[]),
        }}
        columns={columns}
        dataSource={records}
      />
      {!isProjectEnded && canAdd ? (
        <div className='flex my-4 justify-end'>
          <Button
            type='primary'
            onClick={() => {
              setRecords((prev) => [
                ...prev,
                {
                  key: v4(),
                  type: WorkResourceRowType.User,
                  subprojectWorkResourceSelfId: null,
                  userWorkResourceSelfId: null,
                  subprojectName: null,
                  subprojectSelfId: null,
                  departmentId: 0,
                  departmentName: '',
                  email: '',
                  startDate: (resource.startDate ? moment(resource.startDate).format('YYYY-MM-DD') : undefined) as any,
                  endDate: (resource.endDate ? moment(resource.endDate).format('YYYY-MM-DD') : undefined) as any,
                  predictWorkload: null,
                  actualWorkload: null,
                  quarters: [],
                  weeks: [],
                  isCreating: true,
                  parentKey: null,
                },
              ])
            }}
            disabled={!canCreate}
          >
            {t('add resource estimate')}
          </Button>
        </div>
      ) : null}
      <Drawer
        title={openDrawer?.readonly ? '备注详情' : '填写备注'}
        placement='right'
        onClose={() => setOpenDrawer(undefined)}
        visible={!!openDrawer}
      >
        <Input.TextArea
          readOnly={openDrawer?.readonly}
          value={drawerContent}
          onChange={(e) => setDrawerContent(e.target.value)}
          placeholder={t('work.resource.commentPlaceholder')}
          autoSize={{ minRows: 8 }}
        />
        {!openDrawer?.readonly && (
          <div className='flex my-4 justify-end'>
            <Button
              loading={commentLoading}
              type='primary'
              onClick={() => {
                const week = openDrawer?.record?.weeks?.[0]

                commentRun({
                  userWorkResourceSelfId: openDrawer?.record?.userWorkResourceSelfId,
                  year: weekDate.year.toString(),
                  week: weekDate.week.toString(),
                  startDate: weekDate.startDate.format('YYYY-MM-DD'),
                  endDate: weekDate.endDate.format('YYYY-MM-DD'),
                  userWeekWorkResourceId: week?.id,
                  comment: drawerContent,
                })
                setOpenDrawer(undefined)
              }}
            >
              {t('submit')}
            </Button>
          </div>
        )}
      </Drawer>
    </div>
  )
})
